import { apiClient } from './authService';

const customerService = {
  // Get all customers (load full list in one request — DB has a few hundred rows,
  // and CustomerList / AddSale rely on having every customer for client-side search
  // and for picking the "Client Passager" default).
  getAllCustomers: async () => {
    try {
      const response = await apiClient.get('customers/?page_size=5000');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching customers' };
    }
  },

  // Get customer by ID
  getCustomerById: async (id) => {
    try {
      const response = await apiClient.get(`customers/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching customer details' };
    }
  },

  // Create new customer
  createCustomer: async (customerData) => {
    try {
      const response = await apiClient.post('customers/', customerData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error creating customer' };
    }
  },

  // Update customer
  updateCustomer: async (id, customerData) => {
    try {
      const response = await apiClient.put(`customers/${id}/`, customerData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error updating customer' };
    }
  },

  // Partial update (e.g. save a single field like a social profile)
  patchCustomer: async (id, partial) => {
    try {
      const response = await apiClient.patch(`customers/${id}/`, partial);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error updating customer' };
    }
  },

  // Delete customer
  deleteCustomer: async (id) => {
    try {
      const response = await apiClient.delete(`customers/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error deleting customer' };
    }
  },
  
  // The services this client has bought (drives the client-file tabs). Each row
  // carries its attached dossier record (image / note / poids / QR) if any.
  getPurchasedServices: async (id) => {
    try {
      const res = await apiClient.get(`customers/${id}/purchased-services/`);
      return Array.isArray(res.data) ? res.data : (res.data.results || []);
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching purchased services' };
    }
  },

  // Record an acompte against the client's « reste à payer » (pays unpaid sales
  // first, then any opening-balance remainder). Server-side + atomic.
  payCredit: async (id, amount) => {
    try {
      const res = await apiClient.post(`customers/${id}/pay-credit/`, { amount });
      return res.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error paying credit' };
    }
  },

  // Get customer details with sales history
  getCustomerDetails: async (id) => {
    try {
      const response = await apiClient.get(`customers/${id}/details/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching customer details and sales history' };
    }
  },
  
  // Search customers
  searchCustomers: async (query) => {
    try {
      const response = await apiClient.get(`customers/?search=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error searching customers' };
    }
  },

  // Server-side paginated list — used by CustomerList UI.
  getCustomersPage: async ({ page = 1, pageSize = 50, search = '', ordering = '', creditBefore = '', category = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (search) params.set('search', search);
      if (ordering) params.set('ordering', ordering);
      // Filtre « crédit ancien » : ventes impayées datées <= cette date.
      if (creditBefore) params.set('credit_before', creditBefore);
      // Filtre par catégorie client (panneau Filtres de la page clients).
      if (category) params.set('category', category);
      // Hide the walk-in "Client Passager" (id 5) from the clients page + totals.
      params.set('exclude_passager', '1');
      const response = await apiClient.get(`customers/?${params.toString()}`);
      const data = response.data;
      if (Array.isArray(data)) return { results: data, count: data.length, totals: null };
      // `totals` is aggregated server-side over the WHOLE filtered set (not the
      // page), so the stat cards stay correct across search/sort/pagination.
      return { results: data?.results || [], count: data?.count || 0, totals: data?.totals || null };
    } catch (error) {
      console.error('Error fetching customers page:', error);
      throw error.response?.data || { message: 'Error fetching customers' };
    }
  }
};

export default customerService;
